"use client";

import { useEffect, useRef } from "react";
import { Button, Flex } from "antd";
import styles from "./booking.module.css";
import {
  getEligibleSeasonPasses,
  getAllActiveSeasonPasses,
  getSeasonPassesForVenueDates,
} from "@/lib/seasonPassHelpers";
import SeasonPassTicketCard from "./SeasonPassTicketCard";
import { trackSeasonPassView, trackSeasonPassSelect } from "@/lib/analytics";

export function applySeasonPassSelection({
  pass,
  index,
  orderDetails,
  setOrderDetails,
  setTickets,
  options = {},
}) {
  const {
    // On the Tickets step keep "individual" until Next, so the step stays in flow.
    bookingPath = "season_pass",
    replaceTickets = true,
  } = options;
  const qty = Number(pass?.min_per_transaction || 1);
  const venueKey = String(orderDetails?.venue || "").toLowerCase();
  const childEventId = orderDetails?.event_id || null;
  const anchor =
    (pass.coveredEvents || []).find((c) => {
      const cVenue = String(c.venue || "").toLowerCase();
      return (
        venueKey &&
        (cVenue === venueKey ||
          cVenue.includes(venueKey) ||
          venueKey.includes(cVenue))
      );
    }) ||
    (pass.coveredEvents || []).find((c) => c.childEventId === childEventId) ||
    (pass.coveredEvents || [])[0] ||
    null;

  setOrderDetails((prev) => ({
    ...prev,
    booking_path: bookingPath,
    is_season_pass: true,
    date: prev?.date || anchor?.date || null,
    time: prev?.time || anchor?.startTime || null,
    end_date: prev?.end_date || anchor?.endDate || null,
    end_time: prev?.end_time || anchor?.endTime || null,
    timezone: prev?.timezone || anchor?.timezone || null,
    event_id: prev?.event_id || anchor?.childEventId || pass.event_id || null,
    ticket: {
      id: pass.id,
      qty,
      price: pass.price,
      name: pass.name,
      event_id: prev?.event_id || anchor?.childEventId || pass.event_id || null,
      currency: pass.currency,
      requires_approval: pass.requires_approval,
      min_per_transaction: pass.min_per_transaction,
      max_per_transaction: pass.max_per_transaction,
      description: pass.description,
      is_season_pass: true,
      covered_events: pass.coveredEvents,
      event_count: pass.eventCount,
      validity_start: pass.validityStart,
      validity_end: pass.validityEnd,
      index,
    },
    zone: null,
    seating_layout_data: null,
    zone_seats: [],
    seating_layout_event_id: null,
    seating_layout_zone_id: null,
    session_id: null,
    session_created_at: null,
  }));
  if (replaceTickets) setTickets?.([pass]);
}

export default function SeasonPassStep({
  data,
  orderDetails,
  setOrderDetails,
  setActiveStep,
  setTickets,
}) {
  const childEventId = orderDetails?.event_id || null;
  const date = orderDetails?.date || null;
  const venue = orderDetails?.venue || null;
  const city = orderDetails?.address?.city || orderDetails?.city || null;
  const entry = orderDetails?.season_pass_entry;
  const fromDatetimeScreen =
    entry === "datetime_screen" || entry === "datetime";
  const fromVenueScreen = entry === "venue_screen";
  const fromVenue = entry === "venue";

  const passes = fromDatetimeScreen
    ? orderDetails?._venue_dates
      ? getSeasonPassesForVenueDates(data, orderDetails._venue_dates)
      : getAllActiveSeasonPasses(data)
    : fromVenueScreen
      ? getAllActiveSeasonPasses(data)
      : fromVenue
        ? getSeasonPassesForVenueDates(data, orderDetails?._venue_dates)
        : !date && !childEventId
          ? getAllActiveSeasonPasses(data)
          : getEligibleSeasonPasses(data, {
              childEventId,
              date,
              venue: !childEventId && !date ? venue : null,
              city: !childEventId && !date ? city : null,
            });
  const selectedId = orderDetails?.ticket?.id || null;
  const viewedPassIds = useRef(new Set());

  useEffect(() => {
    passes.forEach((pass) => {
      if (viewedPassIds.current.has(pass.id)) return;
      viewedPassIds.current.add(pass.id);
      trackSeasonPassView({
        season_pass_id: pass.id,
        season_pass_name: pass.name,
        number_of_events: pass.eventCount,
        price: pass.price,
        currency: pass.currency,
        event_id: childEventId,
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [passes]);

  const backStep = fromDatetimeScreen
    ? "datetime"
    : fromVenueScreen || fromVenue
      ? "venue"
      : date
        ? "datetime"
        : "venue";

  const goIndividual = () => {
    setOrderDetails((prev) => ({
      ...prev,
      ticket: null,
      booking_path: "individual",
      season_pass_step: false,
      is_season_pass: false,
      season_pass_entry: null,
    }));
    setActiveStep(backStep);
  };

  const selectPass = (pass, index) => {
    trackSeasonPassSelect({
      season_pass_id: pass.id,
      season_pass_name: pass.name,
      price: pass.price,
      currency: pass.currency,
      event_id: childEventId,
    });

    applySeasonPassSelection({
      pass,
      index,
      orderDetails,
      setOrderDetails,
      setTickets,
    });
    setOrderDetails((prev) => ({
      ...prev,
      season_pass_step: true,
    }));
  };

  const handlePassQuantity = (pass, nextQty) => {
    setOrderDetails((prev) => {
      if (!prev?.ticket || prev.ticket.id !== pass.id) return prev;
      return {
        ...prev,
        ticket: { ...prev.ticket, qty: nextQty },
      };
    });
  };

  return (
    <div className={styles.ticketSelection}>
      <Flex vertical gap={20} style={{ width: "100%" }}>
        <h3 className={styles.sectionTitle2}>
          Select Your Multi-Day & Multi-City Season Pass
        </h3>

        {passes.length === 0 ? (
          <div className="rounded-xl border border-[#e3e6e9] bg-[#f8f9fb] px-4 py-6 text-center">
            <p className="m-0 text-sm text-[rgba(2,21,38,0.7)]">
              No active season passes are available.
            </p>
            <Button type="primary" className="mt-4" onClick={goIndividual}>
              Back
            </Button>
          </div>
        ) : (
          <Flex vertical gap={16}>
            {passes.map((pass, index) => (
              <SeasonPassTicketCard
                key={pass.id}
                pass={pass}
                index={index}
                selected={selectedId === pass.id}
                quantity={
                  selectedId === pass.id
                    ? Number(orderDetails?.ticket?.qty || pass?.min_per_transaction || 1)
                    : 0
                }
                onSelect={(pass) => selectPass(pass, index)}
                onQuantityChange={handlePassQuantity}
                highlightChildEventId={childEventId}
                highlightDate={date}
                highlightTime={orderDetails?.time}
              />
            ))}
          </Flex>
        )}
      </Flex>
    </div>
  );
}
